import React, { useState, useEffect, useRef } from "react";
import { PageFlip } from "page-flip";
import * as pdfjsLib from "pdfjs-dist";
import "./FlipbookUltra.scss";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs";

const FlipbookUltra = ({
  pdfUrl,
  title = "Flipbook",
  desktopBreakpoint = 900,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < desktopBreakpoint);
  const [loading, setLoading] = useState(true);
  const [renderedPages, setRenderedPages] = useState({});
  const [allPagesReady, setAllPagesReady] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panOffset = useRef({ x: 0, y: 0 });
  const pageFlipRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const autoPlayInterval = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < desktopBreakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [desktopBreakpoint]);

  useEffect(() => {
    loadPDF();
    return () => {
      if (autoPlayInterval.current) clearInterval(autoPlayInterval.current);
      if (pageFlipRef.current) {
        pageFlipRef.current.destroy();
        pageFlipRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl]);

  useEffect(() => {
    if (allPagesReady && containerRef.current) {
      if (pageFlipRef.current) {
        pageFlipRef.current.destroy();
        pageFlipRef.current = null;
      }
      setTimeout(() => initPageFlip(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPagesReady]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith("#p=") && totalPages > 0 && pageFlipRef.current) {
      const pageNum = parseInt(hash.substring(3), 10);
      if (pageNum > 0 && pageNum <= totalPages) {
        pageFlipRef.current.flip(pageNum - 1);
      }
    }
  }, [totalPages]);

  const initPageFlip = () => {
    if (!containerRef.current) return;

    const pageFlip = new PageFlip(containerRef.current, {
      width: isMobile ? 300 : 500,
      height: isMobile ? 400 : 700,
      size: "stretch",
      minWidth: 315,
      maxWidth: 1000,
      minHeight: 400,
      maxHeight: 1533,
      showCover: true,
      mobileScrollSupport: false,
      drawShadow: true,
      flippingTime: 1000,
      usePortrait: isMobile,
      startZIndex: 0,
      autoSize: true,
      maxShadowOpacity: 0.5,
      showPageCorners: false,
      disableFlipByClick: true,
      useMouseEvents: false,
      swipeDistance: 99999,
    });

    pageFlip.loadFromHTML(document.querySelectorAll(".page-content"));

    pageFlip.on("flip", (e) => {
      setCurrentPage(e.data);
      window.location.hash = `p=${e.data + 1}`;
    });

    pageFlipRef.current = pageFlip;
  };

  const loadPDF = async () => {
    try {
      setLoading(true);
      const pdf = await pdfjsLib.getDocument({
        url: pdfUrl,
        cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/cmaps/",
        cMapPacked: true,
        standardFontDataUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/standard_fonts/",
      }).promise;
      setTotalPages(pdf.numPages);

      const pages = {};
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        await page.render({
          canvasContext: context,
          viewport,
        }).promise;
        pages[i] = canvas.toDataURL("image/png");
      }

      setRenderedPages(pages);
      setLoading(false);
      setAllPagesReady(true);
    } catch (error) {
      console.error("Erro ao carregar PDF:", error);
      setLoading(false);
    }
  };

  const prevPage = () => {
    if (!pageFlipRef.current) return;
    if (pageFlipRef.current.getState() === "flipping") return;
    if (isMobile) {
      const curr = pageFlipRef.current.getCurrentPageIndex();
      if (curr > 0) pageFlipRef.current.turnToPage(curr - 1);
    } else {
      pageFlipRef.current.flipPrev("top");
    }
  };
  const nextPage = () => {
    if (!pageFlipRef.current) return;
    if (pageFlipRef.current.getState() === "flipping") return;
    if (isMobile) {
      const curr = pageFlipRef.current.getCurrentPageIndex();
      if (curr < totalPages - 1) pageFlipRef.current.turnToPage(curr + 1);
    } else {
      pageFlipRef.current.flipNext("top");
    }
  };

  const toggleAutoPlay = () => {
    if (isAutoPlay) {
      clearInterval(autoPlayInterval.current);
      setIsAutoPlay(false);
    } else {
      setIsAutoPlay(true);
      autoPlayInterval.current = setInterval(() => {
        if (pageFlipRef.current) {
          const nextP = currentPage + 1;
          if (nextP < totalPages) {
            pageFlipRef.current.flipNext();
          } else {
            clearInterval(autoPlayInterval.current);
            setIsAutoPlay(false);
          }
        }
      }, 3000);
    }
  };

  const handleDownload = () => window.open(pdfUrl, "_blank");

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      alert("Link copiado para a área de transferência!");
    }
  };

  const handleZoom = (delta) => {
    const newZoom = Math.max(1, Math.min(3, zoom + delta));
    setZoom(newZoom);
    if (newZoom === 1) setPan({ x: 0, y: 0 });
  };

  const handlePanStart = (e) => {
    if (e.target.closest("button")) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    panStart.current = { x: clientX, y: clientY };

    if (zoom > 1) {
      setIsPanning(true);
      panOffset.current = { ...pan };
      e.preventDefault();
    }
  };

  const handlePanMove = (e) => {
    if (zoom > 1 && isPanning) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setPan({
        x: panOffset.current.x + (clientX - panStart.current.x),
        y: panOffset.current.y + (clientY - panStart.current.y),
      });
    }
  };

  const handlePanEnd = (e) => {
    if (zoom > 1) {
      setIsPanning(false);
      return;
    }
    if (!isMobile || !pageFlipRef.current) return;
    const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const diff = clientX - panStart.current.x;
    const threshold = 50;
    if (diff < -threshold) {
      nextPage();
    } else if (diff > threshold) {
      prevPage();
    }
  };

  if (loading) {
    return <div className="flipbook-loading">Carregando Journal...</div>;
  }

  return (
    <div className="flipbook-container">
      <div className="flipbook-header">
        <h2>{title}</h2>
        <div className="flipbook-controls">
          <button className="bt-prev" onClick={prevPage} disabled={currentPage === 0} title="Página anterior">◀</button>
          <span>{currentPage + 1} / {totalPages}</span>
          <button className="bt-next" onClick={nextPage} disabled={currentPage >= totalPages - 1} title="Próxima página">▶</button>
          <button className="bt-play" onClick={toggleAutoPlay} title={isAutoPlay ? "Parar" : "Auto Play"}>{isAutoPlay ? "⏸" : "▶️"}</button>
          <button className="bt-zoom-out" onClick={() => handleZoom(-0.1)} title="Diminuir zoom">🔍-</button>
          <button className="bt-zoom-in" onClick={() => handleZoom(0.1)} title="Aumentar zoom">🔍+</button>
          <button className="bt-download" onClick={handleDownload} title="Download PDF">⬇️</button>
          <button className="bt-share" onClick={handleShare} title="Compartilhar">🔗</button>
        </div>
      </div>

      <div className="flipbook-viewer">
        <button className="side-arrow side-arrow-left" onClick={prevPage} disabled={currentPage === 0} title="Página anterior">&#8249;</button>

        <div
          className="flipbook-wrapper"
          ref={wrapperRef}
          onMouseDown={handlePanStart}
          onMouseMove={handlePanMove}
          onMouseUp={handlePanEnd}
          onMouseLeave={handlePanEnd}
          onTouchStart={handlePanStart}
          onTouchMove={handlePanMove}
          onTouchEnd={handlePanEnd}
          style={{ cursor: zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default" }}
        >
          <div
            ref={containerRef}
            className="flipbook-pageflip"
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: "center center",
            }}
          >
            {Object.keys(renderedPages).map((pageNum) => (
              <div key={pageNum} className="page-content">
                <img src={renderedPages[pageNum]} alt={`Página ${pageNum}`} draggable={false} />
              </div>
            ))}
          </div>
        </div>

        <button className="side-arrow side-arrow-right" onClick={nextPage} disabled={currentPage >= totalPages - 1} title="Próxima página">&#8250;</button>
      </div>
    </div>
  );
};

export default FlipbookUltra;
